/** Third party imports */
import { isPlatformBrowser } from '@angular/common';
import {
    AfterViewInit,
    ApplicationRef,
    Component,
    createComponent,
    DestroyRef,
    effect,
    ElementRef,
    EnvironmentInjector,
    inject,
    input,
    OnDestroy,
    output,
    PLATFORM_ID,
    signal,
    TemplateRef,
    untracked,
    viewChild,
    ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/** Turf and leaflet imports */
import type * as turfType from '@turf/turf';
import type * as LType from 'leaflet';

/** Local imports */
import { calculateArea } from '@shared/utils';
import { ConfirmationModalComponent } from '../modals/confirmation-modal/confirmation-modal.component';
import { BaseCustomControlComponent } from './components/base-custom-control/base-custom-control.component';
import { CREATE_USER_WARNING_MESSAGE, MAP_CUSTOM_CONTROLS } from './constants';
import { MapControl, TerritoryUser } from './interface';
import { MapControlsService, TerritoryMapService } from './services';

/**
 * This is territory map component where users can do the following:
 *
 * - Draw a territory on the map (create dealer user process)
 * - See all selected territories (locator view)
 * - See interested territories (create dealer user process)
 */
@Component({
    selector: 'app-territory-map',
    imports: [ConfirmationModalComponent],
    templateUrl: './territory-map.component.html',
    styleUrl: './territory-map.component.scss',
})
export class TerritoryMapComponent implements AfterViewInit, OnDestroy {
    /**
     * Platform ID for checking browser/server context
     */
    private platformId = inject(PLATFORM_ID);

    /**
     * Inject environment injector used in passing injecter reference to dynamically created and rendered components
     */
    private injector = inject(EnvironmentInjector);

    /**
     * Inject application injector to create custom controls component
     */
    private appInjector = inject(ApplicationRef);

    /**
     * Inject destroy ref instance to auto-unsubscribe fromrxjs subscriptions
     */
    private destroyRef = inject(DestroyRef);

    /**
     * ViewContainerRef used to render consumer-provided popup templates
     */
    private viewContainerRef = inject(ViewContainerRef);

    /**
     * Inject the provided custom map controls (if there are) using our own custom token
     *
     * @description
     * The injected custom controls component passed from the consuming component `providers`' configuration
     */
    private customControls = inject(MAP_CUSTOM_CONTROLS, { optional: true });

    /**
     * Inject territory map service to listen when events such as clicking "next" is triggered in the consuming parent to validate territory intersections
     */
    private territoryService = inject(TerritoryMapService);

    /**
     * Inject map controls service to listen when external custom controls events are emitted
     */
    private mapControlsService = inject(MapControlsService);

    /**
     * Taken territories for the locator variant — populated via real API (TOBEIMPLEMENTED)
     */
    protected takenTerritoriesForView = signal<TerritoryUser[]>([]);

    /**
     * Leaflet instance reference (We did not import it directly since it uses leaflet there directly and leaflet only works on the browser)
     */
    protected LInstance: typeof LType | null = null;

    /**
     * Turf instance reference (We did not import it directly since it uses window)
     */
    protected turfInstance: typeof turfType | null = null;

    /**
     * First marker custom icon
     *
     * NOTICE: We don't import the constant file directly since it's using leaflet there directly and leaflet only works on the browser
     * because if you do, you'll get an error "window is not defined"
     */
    protected firstMarkerIcon: L.DivIcon | null = null;

    /**
     * Default marker custom icon
     *
     * NOTICE: See the `firstMarkerIcon` comment please as to why this is a nullable property
     */
    protected defaultMarkerIcon: L.DivIcon | null = null;

    /**
     * Custom map controls
     */
    protected mapControls: MapControl[] = [];

    /**
     * Map container reference for the leaflet
     */
    protected mapContainer = viewChild<ElementRef<HTMLElement>>('mapContainer');

    /**
     * Map instance reference
     */
    protected map = signal<L.Map | null>(null);
    private initialCenter: LType.LatLngExpression | null = null;
    private initialZoom: number | null = null;

    /**
     * Read-only polygon for `variant === 'view'` (name-territory preview). Used to refit on resize/coordinate updates.
     */
    private staticPreviewPolygon: LType.Polygon | null = null;

    private staticViewInteractionsDisabled = false;

    private postLayoutFitTimeoutId: ReturnType<typeof setTimeout> | null = null;

    /**
     * Whether the polygon is closed (completed the territory marking)
     */
    protected isClosed = signal<boolean>(false);

    /**
     * Marker and its latlang value Map array
     */
    protected markerAndLatLang = signal<Map<L.Marker, L.LatLng>>(new Map<L.Marker, L.LatLng>());

    /**
     * Closing marker and its points (Used this specifically when performing undoeing). This should be nullable
     */
    protected closingMarkerAndPoints = signal<LType.Marker<any> | null>(null);

    /**
     * Holds the array or markers (ordered)
     *
     * This is used to do undoing/removing the latest marker and its points (polygon or polyline)
     */
    protected markerArray = signal<LType.Marker<any>[]>([]);

    /**
     * Holds the undoed markers
     *
     * Used to redo / or add the markers back to its previous points
     */
    protected undoedMarkers = signal<{ marker: LType.Marker<any>; latLang: LType.LatLng }[]>([]);

    /**
     * Undo points. Used to track how many times the user has undone the marker (including the closing marker)
     *
     * This is used to determine if the user is undoing the closing marker or not
     */
    protected undoPoints = signal<number>(0);

    /**
     * Territory color. Defaults to `green`
     */
    protected territoryColor = signal<string>('green');

    /**
     * Whether to show the territory overlap modal
     */
    protected showTerritoryOverlapModal = signal<boolean>(false);

    /**
     * Whether the map is fully initialized and ready (Leaflet loaded, map rendered).
     * Consuming parents can track this signal to know when it's safe to call methods like `hydrateTerritoryData`.
     */
    public isMapReady = signal<boolean>(false);

    /** =================================== Inputs =============================== */

    /**
     * Whether this territory map is for creating user or viewing all other territories. Defaults to `create` for creating user territory
     *
     * Type definition
     * - `create`: For creating user territory
     * - `view`: For viewing the territory, no listeners attached, view-only
     * - `locator`: For the locator where we show all mapped territories
     */
    public variant = input<'create' | 'view' | 'locator'>('create');

    /**
     * Coordinates of the territory
     *
     * @description
     * I added this input so that in the name territory sub-step. We will just pass that data to the map from the draft
     * and the map will draw the polygon based on that data which is just a view only polygon (not draggable)
     */
    public coordinates = input<number[][]>([]);

    /**
     * Zoom level when the map is initialized. Defaults to `null` since we have a set zoom level at the map initialization
     *
     * @description
     * Used this to zoom in in the name territory sub-step since we really need to focus on the user's selected territory
     */
    public zoomLevel = input<number | null>(null);

    /**
     * Whether to show the controls, both the custom and the default leaflet controls
     */
    public showControls = input<boolean>(true);

    /**
     * Custom and fixed height for the map in pixel unit. Defaults to `null` which means it will use the "css-defined" height
     */
    public mapHeight = input<string | null>(null);

    /**
     * Whether to add border around the map or not
     */
    public hasBorder = input<boolean>(false);

    /**
     * Padding for the map bounds when fitting to a polygon. Defaults to `40`
     */
    public boundsPadding = input<number>(40);

    /**
     * Minimum height for the map container. Defaults to `'300px'`
     */
    public mapMinHeight = input<string>('300px');

    /**
     * Warning message. Defaults to the create user message figma design
     */
    public warningMessage = input<string>(CREATE_USER_WARNING_MESSAGE);

    /**
     * Border radius for the map container. Defaults to `'0'`
     */
    public borderRadius = input<string>('0');

    /**
     * Optional popup template rendered when a territory polygon is clicked.
     * The template receives `$implicit` as the territory's `popupData`.
     *
     * @example
     * // In the consumer template:
     * <app-territory-map [popupTemplate]="myTpl" />
     * <ng-template #myTpl let-data>
     *   <div>{{ data.territoryName }}</div>
     * </ng-template>
     */
    public popupTemplate = input<TemplateRef<unknown> | null>(null);

    /**
     * Whether to automatically attempt to center the map on the user's location on init.
     * Defaults to true.
     */
    public autoCenter = input<boolean>(true);

    /** =================================== Outputs ============================== */

    /**
     * Emit the coordinates when the user is done creating the territory
     */
    public onDone = output<{
        color: string;
        coordinates: number[][];
        undoedMarkers: { marker: LType.Marker<any>; latLang: LType.LatLng }[];
        undoPoints: number;
        area: number;
    }>();

    /**
     * Emit when delete button is clicked
     */
    public onDelete = output<void>();

    constructor() {
        // Effect to draw lines when user creates a new marker
        effect((onCleanup) => {
            const markersMap = this.markerAndLatLang();
            const map = this.map();
            const L = this.LInstance;

            if (markersMap.size < 2 || !map || !L) return;

            // Make the delete button active if there are more than 2 markers
            if (markersMap.size >= 2) {
                const deleteButton = document.getElementById('delete-btn');
                deleteButton?.setAttribute('data-delete-btn--disabled', 'false');
            }

            const allMarkerPoints = Array.from(markersMap.values());

            // Create the new shape
            let newShape: any;

            if (this.isClosed()) {
                newShape = L.polygon(allMarkerPoints, {
                    color: this.territoryColor(),
                    fillColor: this.territoryColor(),
                    fillOpacity: 0.5,
                    weight: 2,
                }).addTo(map);
            } else {
                newShape = L.polyline(allMarkerPoints, {
                    color: this.territoryColor(),
                    dashArray: '5, 5',
                    weight: 2,
                }).addTo(map);
            }

            // CLEANUP: This runs before the next effect execution
            onCleanup(() => {
                newShape.remove();
            });
        });

        /**
         * Effect to run on marker drag
         *
         * Reason I did not merge on the first effect since as much as possible, I try to make a single function do one thing ONLY
         */
        effect((onCleanup) => {
            const isClosed = this.isClosed();
            let allMarkers: LType.Marker<any>[] = [];

            // We only allow dragging if the polygon is closed
            if (isClosed) {
                // UNTRACKED: We only want to enable dragging when the polygon CLOSES.
                // We do NOT want this effect to re-run every time a marker's coordinate updates,
                // otherwise it will constantly re-bind the 'drag' listener and break the interaction.
                allMarkers = untracked(() => [...this.markerAndLatLang().keys()]);

                allMarkers.forEach((marker) => {
                    marker.dragging?.enable();

                    marker.on('drag', (e) => {
                        const newLatLang = e.target.getLatLng();
                        this.markerAndLatLang.update((markerMap) => {
                            markerMap.set(marker, newLatLang);
                            return new Map(markerMap);
                        });
                    });
                });
            }
            onCleanup(() => {
                allMarkers.forEach((marker) => {
                    marker.off('drag');
                    marker.dragging?.disable();
                });
            });
        });

        // Effect to keep TerritoryMapService.markerCount in sync with the current marker array length
        effect(() => {
            this.territoryService.markerCount.set(this.markerArray().length);
        });

        // Effect to fly/fit the map to requested bounds
        effect(() => {
            const bounds = this.territoryService.flyToBounds();
            const map = this.map();
            const L = this.LInstance;

            if (!bounds || !map || !L) return;

            const latLngs = bounds.map(([lat, lng]) => L.latLng(lat, lng));
            const leafletBounds = L.latLngBounds(latLngs);
            map.flyToBounds(leafletBounds, { padding: [60, 60], maxZoom: 10, duration: 0.8 });
        });

        /**
         * Keep static `view` preview (draft polygon) synced with inputs and framed with fitBounds
         * after layout / CSS transitions so the map is not stuck at the default US zoom.
         */
        effect(() => {
            const ready = this.isMapReady();
            const variant = this.variant();
            const coords = this.coordinates();
            const mapInstance = this.map();

            if (!ready || variant !== 'view') {
                untracked(() => this.teardownStaticTerritoryPreview());
                return;
            }

            if (!mapInstance || !this.LInstance || coords.length < 3) {
                untracked(() => this.teardownStaticTerritoryPreview());
                return;
            }

            const L = this.LInstance;
            const map = mapInstance;
            const coordsSnapshot = coords.map((row) => [...row]);

            untracked(() => {
                requestAnimationFrame(() => {
                    map.invalidateSize();
                    this.renderStaticPolygon(L, map, coordsSnapshot);
                    requestAnimationFrame(() => {
                        map.invalidateSize();
                        this.fitMapToStaticPreviewPolygon(map);
                    });
                });
            });
        });

        // Effect to emit the coordinates when the user is done creating the territory
        effect(() => {
            const isClosed = this.isClosed();
            const markerAndLatLang = this.markerAndLatLang();

            // We only emit the coordinates if the user is creating a new territory, not when the user is just viewing the territory using locator or normal view
            if (isClosed && markerAndLatLang.size >= 3 && this.variant() === 'create') {
                const coordinates = Array.from(markerAndLatLang.values()).map((latLang) => [
                    latLang.lat,
                    latLang.lng,
                ]);

                // Calculate the area of the polygon
                const area = calculateArea(coordinates);

                // We also emit the undoedMarkers and undoPoints so we can use hydrate these values if user navigates back to the create territory step
                this.onDone.emit({
                    color: this.territoryColor(),
                    coordinates,
                    undoedMarkers: this.undoedMarkers(),
                    undoPoints: this.undoPoints(),
                    area,
                });
            }
        });

        // Effect to listen when the territory validation is triggered
        effect(() => {
            const triggerValidatationId = this.territoryService.triggerValidatationId();
            const doesIntersect = this.territoryService.territoryIntersect();
            if (triggerValidatationId && this.isClosed() && doesIntersect) {
                this.showTerritoryOverlapModal.set(true);
            }
        });
    }

    /**
     * Initialize map on view init
     */
    public ngAfterViewInit(): void {
        if (isPlatformBrowser(this.platformId)) {
            // We only import the constant here since constant uses leaflet and leaflet only works in the browser
            // We also need to import leaflet dynamically to avoid top-level 'window is not defined' error
            Promise.all([
                import('leaflet'),
                import('@turf/turf'),
                import('./constants/territory-map.constant'),
            ]).then(
                async ([
                    leaflet,
                    turf,
                    { FIRST_MARKER_ICON_OPTIONS, DEFAULT_MARKER_ICON_OPTIONS, MAP_CONTROLS },
                ]) => {
                    const L = ('default' in leaflet ? leaflet.default : leaflet) as typeof LType;
                    this.turfInstance = turf;
                    this.LInstance = L;
                    this.firstMarkerIcon = L.divIcon(FIRST_MARKER_ICON_OPTIONS);
                    this.defaultMarkerIcon = L.divIcon(DEFAULT_MARKER_ICON_OPTIONS);
                    this.mapControls = MAP_CONTROLS;

                    await this.initializeMap(L);
                    this.observeMapHeight();
                    this.listenToActions();
                    this.isMapReady.set(true); // This allows the parent component effect to re-run and hydrate the map
                },
            );
        }
    }

    /**
     * Clear map on destroy
     */
    public ngOnDestroy(): void {
        if (this.postLayoutFitTimeoutId !== null) {
            clearTimeout(this.postLayoutFitTimeoutId);
            this.postLayoutFitTimeoutId = null;
        }
        this.map()?.remove();
    }

    /**
     * Initialize map
     * @private
     */
    private async initializeMap(L: typeof LType): Promise<void> {
        const mapContainer = this.mapContainer()?.nativeElement;
        if (!mapContainer) {
            return;
        }

        // Set z-index so its controls won't go above the modals
        mapContainer.style.zIndex = '10';

        // If map height is specified, set it to the map container
        if (this.mapHeight()) {
            mapContainer.style.height = this.mapHeight()!;
        }

        if (this.hasBorder()) {
            mapContainer.style.border = '2px solid #92c347';
            mapContainer.style.borderRadius = '8px';
            mapContainer.style.overflow = 'hidden';
            mapContainer.style.boxShadow =
                '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
            mapContainer.style.overflow = 'hidden';
        }

        // Use fallback center immediately so map renders fast (geolocation may take too long or be blocked).
        const centerView: [number, number] = [39.8283, -98.5795];
        const zoomLevel = this.variant() === 'create' ? 7 : 5;
        const finalZoomLevel = this.zoomLevel() ?? zoomLevel;

        const map = L.map(mapContainer, {
            zoomControl: this.variant() === 'create' ? false : this.showControls(), // We set the zoom control to false since we are using custom controls
            minZoom: 3,
            maxBounds: [
                [-90, -180],
                [90, 180],
            ],
            maxBoundsViscosity: 1.0,
        }).setView(centerView as LType.LatLngExpression, finalZoomLevel);

        // Store initial view for reset action
        this.initialCenter = centerView as LType.LatLngExpression;
        this.initialZoom = finalZoomLevel;

        // Geolocation is best-effort and should not block first paint.
        // Recenter only when map has no plotted territory to focus on.
        if (this.autoCenter()) {
            this.centerToUserLocation();
        }

        // We're using Google Roadmap tiles (Standard Google Maps)
        L.tileLayer('https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}', {
            maxZoom: 20,
            attribution: '&copy; Google',
        }).addTo(map);

        // Set the map
        this.map.set(map);

        /**
         * If external custom controls are provided, we will use it instead of the internal custom controls
         *
         * We inject the provided custom controls component and created a leaflet custom control instance and added it to the map
         */
        if (this.customControls) {
            const injectedCustomControls = L.Control.extend({
                options: {
                    position: 'topleft',
                },
                onAdd: () => {
                    const container = L.DomUtil.create('div');

                    const ref = createComponent(this.customControls!, {
                        environmentInjector: this.injector,
                        hostElement: container,
                    });

                    this.appInjector.attachView(ref.hostView);

                    L.DomEvent.disableClickPropagation(container);

                    // We disable the click propogation on the container so it would not propogate the event down/up to the map itself
                    L.DomEvent.disableClickPropagation(container);
                    return container;
                },
            });

            new injectedCustomControls().addTo(map);
        }

        // Add our own internal custom control to the map
        // The component is just absracted but this is the default custom control for this map
        const customControl = L.Control.extend({
            options: {
                position: 'topleft',
            },
            onAdd: () => {
                const container = L.DomUtil.create('div', 'control-custom');
                const ref = createComponent(BaseCustomControlComponent, {
                    environmentInjector: this.injector,
                    hostElement: container,
                });

                this.appInjector.attachView(ref.hostView);

                // We disable the click propogation on the container so it would not propogate the event down/up to the map itself
                L.DomEvent.disableClickPropagation(container);
                return container;
            },
        });

        /**
         * Here we append our own internal custom controls to the map
         *
         * Append the custom controls to the map. But only append it for 'create' variant and no customControl is provided
         */
        if (this.variant() === 'create' && !this.customControls) {
            new customControl().addTo(map);
        }

        // This is crucial for leaflet to correctly calculate the container size if it was initialized
        // while the container was still being rendered or had 0 height.
        setTimeout(() => {
            map.invalidateSize();

            if (this.variant() === 'locator') {
                this.initializeAllTakenTerritories();
            }
        }, 100);
    }

    /**
     * Initialize all taken territories (For "locator" variant)
     * @private
     */
    private initializeAllTakenTerritories(): void {
        // We loop through each taken territories and initialize them on the map
        this.territoryService.locatorTerritories().forEach((takenTerritory) => {
            this.initializeTakenTerritory(takenTerritory);
        });
    }

    /**
     * Initialize taken territory on map
     * @private
     */
    private initializeTakenTerritory(userTerritory: TerritoryUser): void {
        const map = this.map();
        if (!this.LInstance || !map) {
            return;
        }

        // We create a polygon from the taken territory coordinates
        const polygon = this.LInstance.polygon(userTerritory.territory, {
            color: userTerritory.color,
            weight: 2,
            opacity: 0.5,
            fillColor: userTerritory.color,
            fillOpacity: 0.5,
        }).addTo(map);

        // If consumer provided a popup template, open it on polygon click
        if (this.popupTemplate()) {
            polygon.on('click', (e) => {
                this.openTerritoryPopup(e.latlng, userTerritory);
            });

            // Pointer cursor to hint it's clickable
            polygon.getElement()?.classList.add('territory-polygon--clickable');
        }
    }

    /**
     * Renders the consumer-provided popup template with the territory's popupData
     * and opens it as a Leaflet popup at the clicked location.
     */
    private openTerritoryPopup(latlng: LType.LatLng, territory: TerritoryUser): void {
        const map = this.map();
        const L = this.LInstance;
        const template = this.popupTemplate();

        if (!map || !L || !template) return;

        // Create a container and render the Angular template into it
        const container = document.createElement('div');
        const viewRef = this.viewContainerRef.createEmbeddedView(
            template as TemplateRef<{ $implicit: unknown }>,
            {
                $implicit: territory.popupData,
            },
        );
        viewRef.detectChanges();

        viewRef.rootNodes.forEach((node: Node) => container.appendChild(node));

        const popup = L.popup({
            closeButton: true,
            className: 'territory-popup',
            maxWidth: 420,
            minWidth: 320,
            offset: [0, -4],
        })
            .setLatLng(latlng)
            .setContent(container);

        // Destroy the Angular view when the popup is closed
        popup.on('remove', () => viewRef.destroy());

        popup.openOn(map);
    }

    /**
     * Renders a static, non-interactive polygon on the map and fits the view to it.
     * Used in the "name territory" sub-step to display the user's drawn territory as a close-up preview.
     *
     * @param L - Leaflet module reference
     * @param map - The Leaflet map instance
     * @param coordinates - Array of [lat, lng] pairs representing the polygon
     * @private
     */
    private teardownStaticTerritoryPreview(): void {
        if (this.postLayoutFitTimeoutId !== null) {
            clearTimeout(this.postLayoutFitTimeoutId);
            this.postLayoutFitTimeoutId = null;
        }
        if (this.staticPreviewPolygon) {
            this.staticPreviewPolygon.remove();
            this.staticPreviewPolygon = null;
        }
        this.staticViewInteractionsDisabled = false;
    }

    private fitMapToStaticPreviewPolygon(map: LType.Map): void {
        if (!this.staticPreviewPolygon) return;
        const padding = this.boundsPadding();
        map.fitBounds(this.staticPreviewPolygon.getBounds(), {
            padding: [padding, padding],
            maxZoom: 18,
        });
    }

    /**
     * Refit after host layout/CSS entrance animations (e.g. name-territory map panel) so Leaflet uses final size.
     */
    private schedulePostLayoutStaticFit(map: LType.Map): void {
        if (this.postLayoutFitTimeoutId !== null) {
            clearTimeout(this.postLayoutFitTimeoutId);
        }
        this.postLayoutFitTimeoutId = setTimeout(() => {
            this.postLayoutFitTimeoutId = null;
            if (!this.staticPreviewPolygon) return;
            map.invalidateSize();
            this.fitMapToStaticPreviewPolygon(map);
        }, 450);
    }

    private renderStaticPolygon(L: typeof LType, map: LType.Map, coordinates: number[][]): void {
        if (this.staticPreviewPolygon) {
            this.staticPreviewPolygon.remove();
            this.staticPreviewPolygon = null;
        }

        const latLngs = coordinates.map((coord) => [coord[0], coord[1]] as LType.LatLngExpression);

        const polygon = L.polygon(latLngs, {
            color: 'green',
            fillColor: 'green',
            fillOpacity: 0.35,
            weight: 2,
        }).addTo(map);

        this.staticPreviewPolygon = polygon;

        this.fitMapToStaticPreviewPolygon(map);
        this.schedulePostLayoutStaticFit(map);

        if (!this.staticViewInteractionsDisabled) {
            map.dragging.disable();
            map.touchZoom.disable();
            map.doubleClickZoom.disable();
            map.scrollWheelZoom.disable();
            map.boxZoom.disable();
            map.keyboard.disable();
            this.staticViewInteractionsDisabled = true;
        }
    }

    /**
     * Handles making the map view-only. Disables further drawing
     * @private
     */
    private handleViewMode(): void {
        // Remove click listener on the map, make it view only
        this.map()?.off('click');

        // remove the markers event listener as well (GO BACK TO HERE IF QA wants something else, because i can sense it hehe)
        this.markerArray().forEach((marker) => {
            marker.off('click');
        });
    }

    /**
     * Handle start drawing territory on map
     * @private
     */
    private handleStartDrawingTerritoryOnMap(): void {
        const map = this.map();
        if (!map) {
            return;
        }

        // Generate a random color for the territory
        const randomColor = this.generateRandomColor();
        this.territoryColor.set(randomColor);

        // always off listeners before attaching one again
        map.off('click');
        map.on('click', (e) => {
            // If closed then do not draw anymore
            if (this.isClosed()) return;

            // Check if this is the first marker to be created
            const isFirstMarker = this.markerAndLatLang().size === 0;

            /**
             * We used the isFirstMarker property since when undoing we only undo the last marker (if polygon is closed) instead of both of the markers since
             * they have the same marker
             */
            const marker = this.LInstance?.marker(e.latlng, {
                icon: isFirstMarker ? this.firstMarkerIcon! : this.defaultMarkerIcon!,
            }).addTo(map);

            // Update marker and latlang map
            this.markerAndLatLang.update((map) => {
                map.set(marker!, e.latlng);
                return new Map(map);
            });

            // Update the markerAndLatLang array to keep track of the added markers (see comment on the signal property)
            this.markerArray.update((prev) => [...prev, marker!]);

            // Close the polygon when the user clicks the first marker
            if (isFirstMarker) {
                marker?.on('click', this.closePolygon.bind(this));
            }
        });

        // Set the listener to the first marker to close the polygon only if it has no event listener yet to avoid setting multiple listeners
        const firstMarker = this.markerArray()[0];
        if (firstMarker && !firstMarker.hasEventListeners('click')) {
            firstMarker.on('click', this.closePolygon.bind(this));
        }
    }

    /**
     * Handle undo the latest marker (Basically remove the very latest markers)
     * @private
     */
    private handleUndoMarker(): void {
        // Guard: Do not handle undo if no markers yet
        if (this.markerArray().length === 0) return;

        const lastMarker = this.markerArray()[this.markerArray().length - 1];

        this.undoPoints.update((prev) => prev + 1);

        /**
         * If closingMarkerAndPoints is not empty and isClosed is true then it means the polygon was closed, so clicking the undo should only make it isClosed = false
         * and return
         */
        if (this.closingMarkerAndPoints() && this.isClosed() && this.undoPoints() === 1) {
            this.isClosed.set(false);
            return;
        }

        // Remove the marker from the map
        if (lastMarker) {
            lastMarker.remove();

            // Take the marker and its points before deleting so users can redo it later
            const undoedValue = this.markerAndLatLang().get(lastMarker);

            // Save the undoed value to the undoed arrays
            if (undoedValue) {
                this.undoedMarkers.update((prev) => [
                    ...prev,
                    { marker: lastMarker, latLang: undoedValue },
                ]);
            }

            // Update the markerArray to remove the last marker
            this.markerArray.update((prev) => prev.slice(0, -1));

            // Update the markerAndLatLang points to reflect the polygons
            this.markerAndLatLang.update((map) => {
                map.delete(lastMarker);
                return new Map(map);
            });

            // Make sure to the the isClosed back to false
            this.isClosed.set(false);
        }
    }

    /**
     * Handle redo / put back the latest undoed marker
     * @private
     */
    private handleRedoMarker(): void {
        // Do not allow redo if undo points is 0. (see handleUndoMarker)
        if (this.undoPoints() === 0) return;

        // Get the last undoed marker
        const lastUndoedMarker = this.undoedMarkers()[this.undoedMarkers().length - 1];

        // Decrement undo points
        this.undoPoints.update((prev) => prev - 1);

        /**
         * Check if the closingMarkerAndPoints is not empty then we make the isClosed to true and clear the closingMarkerAndPoints and return
         */
        if (this.closingMarkerAndPoints() && this.undoPoints() === 0) {
            this.isClosed.set(true);
            return;
        }

        // If there is a last undoed marker
        if (lastUndoedMarker) {
            // Add the marker back to the map
            lastUndoedMarker.marker.addTo(this.map()!);

            // Remove the undoed marker from the undoedMarker array
            this.undoedMarkers.update((prev) => prev.slice(0, -1));

            // Update the markerArray to add the marker back
            this.markerArray.update((prev) => [...prev, lastUndoedMarker.marker]);

            // Update the markerAndLatLang points to reflect the polygons
            this.markerAndLatLang.update((map) => {
                map.set(lastUndoedMarker.marker, lastUndoedMarker.latLang);
                return new Map(map);
            });

            // Make sure to the the isClosed back to false
            this.isClosed.set(false);
        }
    }

    /**
     * Method attached to the first marker to close the polygon or complete territory map
     * @private
     */
    private closePolygon(): void {
        if (this.markerAndLatLang().size >= 3) {
            const firstMarker = this.markerArray()[0];

            // set the closing marker signal
            this.closingMarkerAndPoints.set(firstMarker);

            // Close the polygon
            this.isClosed.set(true);
        }
    }

    /**
     * Checks if the territory intersects with any taken territories
     * @public
     * @returns {Promise<boolean>} Whether the territory intersects with any taken territories
     */
    public async checkTakenTerritories(): Promise<boolean> {
        if (!this.turfInstance || !this.LInstance) return false;

        // Fetch all the territories from the api (FOR NOW MOCK ONLY - TOBECHANGED | TOBEREMOVED)
        const allTerritories = await this.territoryService.getTakenTerritories();

        const latlangs = Array.from(this.markerAndLatLang().values());
        const coordinates = latlangs.map((latlang) => [latlang.lat, latlang.lng]);
        const turfCoordinates = this.turfInstance.polygon([
            [...coordinates, coordinates[0]].map((coords) => [coords[1], coords[0]]),
        ]);

        const takenTerritories = this.turfInstance.polygon([allTerritories]);

        // Check if part of the territory is taken
        const intersect = this.turfInstance.intersect(
            this.turfInstance.featureCollection([turfCoordinates, takenTerritories]),
        );

        if (intersect) {
            // Clear any existing intersection markers on every click to verify if the new polygon intersects with the taken territories
            this.map()?.eachLayer((layer) => {
                if (this.LInstance && layer instanceof this.LInstance.GeoJSON) {
                    layer.remove();
                }
            });

            this.LInstance.geoJSON(intersect, {
                style: {
                    color: 'blue',
                    dashArray: '5,5',
                    fillOpacity: 0.5,
                    weight: 2,
                },
            }).addTo(this.map()!);

            // Update territory service
            this.territoryService.setTerritoryIntersect(true);
        } else {
            this.map()?.eachLayer((layer) => {
                if (this.LInstance && layer instanceof this.LInstance.GeoJSON) {
                    layer.remove();
                }
            });

            // Update territory service
            this.territoryService.setTerritoryIntersect(false);
        }

        return !!intersect;
    }

    /**
     * Hydrate territory internal states from the consuming (parent) state. eg passed from draft
     * @param data - any for now we'll fix this later
     * @public So it can be accessed in the consuming parent
     */
    public hydrateTerritoryData(data: any): void {
        if (!data?.coordinates || !this.LInstance || !this.map()) return;

        // 1). Clear existing markers from the map if necessary
        this.markerArray().forEach((marker) => {
            marker.remove();
        });
        // Clear the marker array
        this.markerArray.set([]);
        // Clear the marker and latlang map
        this.markerAndLatLang.set(new Map());
        // Reset the isClosed signal
        this.isClosed.set(false);
        // Reset the undoed markers array
        this.undoedMarkers.set([]);
        // Reset undo points
        this.undoPoints.set(0);
        // Delete closingMarkerAndPoints data
        this.closingMarkerAndPoints.set(null);

        // 2). Set the coordinates to the map
        const newMarkers = (data.coordinates as number[][]).map((coordinate, idx) => {
            const marker = this.LInstance?.marker([coordinate[0], coordinate[1]], {
                icon: idx === 0 ? this.firstMarkerIcon! : this.defaultMarkerIcon!,
            });

            marker?.addTo(this.map()!);

            return marker;
        });

        if (newMarkers.length > 0) {
            this.markerArray.set(newMarkers as L.Marker<any>[]);

            const newMap = new Map<L.Marker<any>, L.LatLng>();
            newMarkers.forEach((marker) => {
                newMap.set(marker!, marker!.getLatLng());
            });
            this.markerAndLatLang.set(newMap);
            // Assume that the polygon was already closed
            this.isClosed.set(true);
        }

        // 3). Set the closing marker and points
        const closingMarker = this.markerArray()[0];
        this.closingMarkerAndPoints.set(closingMarker);

        // 4). Set the territory color
        this.territoryColor.set(data.color);

        // Focus map to hydrated territory so reopening the step does not appear zoomed out.
        const map = this.map();
        if (map && this.markerArray().length > 0) {
            const bounds = this.LInstance.latLngBounds(
                this.markerArray().map((marker) => marker.getLatLng()),
            );
            const padding = this.boundsPadding();
            map.fitBounds(bounds, { padding: [padding, padding], maxZoom: 12 });

            // Keep reset action aligned with the currently loaded territory.
            this.initialCenter = map.getCenter();
            this.initialZoom = map.getZoom();
        }
    }

    /**
     * Delete drawing, or selected territories (start from fresh)
     * @private
     */
    private handleDeleteTerritoryDrawing(): void {
        // Remove all markers from the map
        this.markerArray().forEach((marker) => {
            marker.remove();
        });
        // Clear the marker array
        this.markerArray.set([]);
        // Clear the marker and latlang map
        this.markerAndLatLang.set(new Map());
        // Reset the isClosed signal
        this.isClosed.set(false);
        // Reset the undoed markers array
        this.undoedMarkers.set([]);
        // Reset undo points
        this.undoPoints.set(0);
        // Delete closingMarkerAndPoints data
        this.closingMarkerAndPoints.set(null);

        // Clear the territory service territory intersect
        this.territoryService.setTerritoryIntersect(false);

        // Clear the intersection markers
        this.map()?.eachLayer((layer) => {
            if (this.LInstance && layer instanceof this.LInstance.GeoJSON) {
                layer.remove();
            }
        });

        // Disable the delete button once again after deleting the drawing
        const button = this.mapContainer()?.nativeElement.querySelector(`#delete-btn`);
        button?.setAttribute('data-delete-btn--disabled', 'true');

        // Emit the delete event
        this.onDelete.emit();
    }

    /**
     * Close the territory overlap modal
     */
    protected closeTerritoryOverlapModal(): void {
        this.showTerritoryOverlapModal.set(false);

        // Clear the territory service territory intersect
        this.territoryService.setTerritoryIntersect(false);
    }

    /**
     * Listens to emitted actions external custom controls via observable
     */
    private listenToActions() {
        this.mapControlsService.action$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((action) => {
                switch (action) {
                    case 'view':
                        this.handleViewMode();
                        break;
                    case 'draw':
                        this.handleStartDrawingTerritoryOnMap();
                        break;
                    case 'zoom-in':
                        this.map()?.zoomIn();
                        break;
                    case 'zoom-out':
                        this.map()?.zoomOut();
                        break;
                    case 'reset':
                        if (this.initialCenter !== null && this.initialZoom !== null) {
                            this.map()?.setView(this.initialCenter, this.initialZoom);
                        }
                        break;
                    case 'undo':
                        this.handleUndoMarker();
                        break;
                    case 'redo':
                        this.handleRedoMarker();
                        break;
                    case 'delete':
                        this.handleDeleteTerritoryDrawing();
                        break;
                }
            });
    }

    /**
     * Observes the map height and updates the territory service map height
     *
     * @remarks
     * This is used to update the territory service map height when the map is resized
     * allowing external custom controls to have access to the map height
     *
     * We also substract 20 (as px) since by default top-left position seems to have padding
     */
    private observeMapHeight() {
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                this.territoryService.mapHeight.set(entry.contentRect.height - 20);
            }

            const map = this.map();
            if (this.variant() === 'view' && this.staticPreviewPolygon && map) {
                map.invalidateSize();
                this.fitMapToStaticPreviewPolygon(map);
            }
        });

        resizeObserver.observe(this.mapContainer()?.nativeElement!);
    }

    /**
     * Get current latlang based on user location
     * @private
     * @returns Promise<[number, number]>
     */
    private getCurrentLocation(): Promise<[number, number]> {
        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve([position.coords.latitude, position.coords.longitude]);
                },
                (error) => {
                    reject(error);
                },
                {
                    enableHighAccuracy: false,
                    timeout: 1500,
                    maximumAge: 60000,
                },
            );
        });
    }

    /**
     * Best-effort centering to current user location when there is no plotted territory.
     * Does not interrupt normal flow if denied/timed out.
     * @public
     */
    public centerToUserLocation(): void {
        const variant = this.variant();
        const isSupportedVariant = variant === 'create' || variant === 'locator';
        if (!isSupportedVariant) {
            return;
        }

        this.getCurrentLocation()
            .then((currentLocation) => {
                const map = this.map();
                if (!map) {
                    return;
                }

                // Avoid overriding hydrated or already drawn territory focus.
                const hasDrawnCoordinates =
                    this.markerArray().length > 0 || this.coordinates().length > 0;
                if (hasDrawnCoordinates) {
                    return;
                }

                const targetZoom = Math.max(map.getZoom(), 13);
                map.setView(currentLocation as LType.LatLngExpression, targetZoom);
                this.initialCenter = currentLocation as LType.LatLngExpression;
                this.initialZoom = targetZoom;
            })
            .catch(() => {
                // No-op by design: fallback center is already rendered.
            });
    }

    /**
     * Generates a random color for territory creation
     * @private
     */
    private generateRandomColor(): string {
        const hue = Math.floor(Math.random() * 360);
        return `hsl(${hue}, 70%, 50%)`;
    }
}
